import { z } from 'zod';
import {
  BooleanFilter,
  booleanFilterSchema
} from 'v5/api/_common/query/filters/BooleanFilter';
import {
  paginationSchema,
  paginationUtils
} from 'v5/api/_common/query/pagination';
import {
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSorter,
  withSorterUtils
} from 'v5/api/_common/query/queryUtils';
import type { Sorter } from 'v5/api/_common/query/sorters/Sorter';
import type { Nullable } from 'v5/platform/typescript';
import type { ResourceTypeAdmin } from './model';

export const RESOURCE_TYPE_ADMIN_SORT_FIELDS = [
  'name',
  'type',
  'workspaces'
] as const;

export type ResourceTypeAdminSortField =
  (typeof RESOURCE_TYPE_ADMIN_SORT_FIELDS)[number];

export type ResourceTypeAdminListQuery = WithPagination &
  WithSorter<ResourceTypeAdminSortField> &
  WithOptionalFilters<{ archived: BooleanFilter }>;

export const resourceTypeAdminListQueryUtils = withBuilderUtils({
  ...withFiltersUtils<ResourceTypeAdminListQuery>(),
  ...withPaginationUtils<ResourceTypeAdminListQuery>(),
  ...withSorterUtils<ResourceTypeAdminListQuery>(),
  ofEmpty: (): ResourceTypeAdminListQuery => ({
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): ResourceTypeAdminListQuery => ({
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  })
}).setInitialQueryFactory(x => x.ofDefault);

export const initialResourceTypeAdminListQuery =
  resourceTypeAdminListQueryUtils.ofDefault();

/** qs turns `null` into `sort=`; treat empty / junk as null. */
const strToNull = (value: unknown) =>
  value === '' || value === '-' ? null : value;

const archivedFilterSchema = z.preprocess(raw => {
  if (raw == null || typeof raw !== 'object') return raw;
  const record = raw as Record<string, unknown>;
  // Normalize legacy / mistyped operators (e.g. "equals") to "equal".
  return {
    operator: 'equal',
    value: record.value
  };
}, booleanFilterSchema);

export const resourceTypeAdminListQuerySchema = z
  .object({
    filters: z
      .object({
        archived: archivedFilterSchema.optional()
      })
      .partial()
      .optional(),
    page: paginationSchema.optional(),
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(RESOURCE_TYPE_ADMIN_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .optional()
  })
  .transform(
    (value): ResourceTypeAdminListQuery => ({
      sort: value.sort ?? null,
      page: value.page ?? paginationUtils.ofDefault(),
      filters: {
        archived: value.filters?.archived ?? {
          operator: 'equal',
          value: false
        }
      }
    })
  )
  .catch(() => initialResourceTypeAdminListQuery);

export function isArchivedListQuery(
  query: ResourceTypeAdminListQuery
): boolean {
  return query.filters?.archived?.value ?? false;
}

export function sortResourceTypes(
  types: ResourceTypeAdmin[],
  sort: Nullable<Sorter<ResourceTypeAdminSortField>>
): ResourceTypeAdmin[] {
  if (sort == null) {
    return [...types].sort((a, b) => a.order - b.order);
  }
  const dir = sort.order === 'desc' ? -1 : 1;
  return [...types].sort((a, b) => {
    switch (sort.field) {
      case 'name':
        return (
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * dir
        );
      case 'type':
        return (
          a.mechanism.localeCompare(b.mechanism, undefined, {
            sensitivity: 'base'
          }) * dir
        );
      default:
        return a.order - b.order;
    }
  });
}
